import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function TagInput({
  value = '',
  onChange,
  placeholder,
  variant = 'green',
  label,
  maxLength,
  placeholderFontSize = '15px',

}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const parseTags = (csv) =>
    (csv || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const [tags, setTags] = useState(() => parseTags(value));
  const [inputValue, setInputValue] = useState('');
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const placeholderText = placeholder || t('Ajouter un terme ou une expression');
  const isEmptyState = tags.length === 0 && inputValue.length === 0;
  const serializedTags = tags.join(', ');
  const serializedWithInput =
    tags.length > 0 && inputValue
      ? `${serializedTags}, ${inputValue}`
      : `${serializedTags}${inputValue}`;
  const currentLength = serializedWithInput.length;
  const isLimitReached = Boolean(maxLength) && currentLength >= maxLength;

  useEffect(() => {
    const incoming = parseTags(value);
    const current = tags.join(',');
    const next = incoming.join(',');
    if (current !== next) setTags(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emitChange = (nextTags) => {
    if (onChange) onChange(nextTags.join(', '));
  };

  const addTag = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const candidates = trimmed
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (candidates.length === 0) return;

    const newOnes = candidates.filter((s) => !tags.includes(s));
    const hasOnlyDuplicates =
      newOnes.length === 0 && candidates.some((s) => tags.includes(s));

    if (hasOnlyDuplicates) {
      setShowDuplicateWarning(true);
      return;
    }

    if (!newOnes.length) return;

    let next = [...tags];

    newOnes.forEach((candidate) => {
      const candidateTags = [...next, candidate];
      const candidateSerialized = candidateTags.join(', ');
      if (!maxLength || candidateSerialized.length <= maxLength) {
        next = candidateTags;
      }
    });

    if (next.join(', ') === tags.join(', ')) {
      setInputValue('');
      return;
    }

    setShowDuplicateWarning(false);
    setTags(next);
    emitChange(next);
    setInputValue('');
  };

  const removeTag = (index) => {
    setShowDuplicateWarning(false);
    const next = tags.filter((_, i) => i !== index);
    setTags(next);
    emitChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) addTag(inputValue);
  };

  const colors =
    variant === 'green'
      ? {
        tagBg: '#EAF3DE',
        tagBorder: '#C5DFA8',
        tagText: '#3A6320',
        tagXColor: '#5A8A35',
      }
      : {
        tagBg: '#FCEBEB',
        tagBorder: '#F2BABA',
        tagText: '#8B2020',
        tagXColor: '#B84040',
      };

  return (
    <div className="mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {label && (
        <div className="label-title" style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          {label}
        </div>
      )}

      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px',
          minHeight: '70px',
          padding: '8px 12px',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          backgroundColor: '#F8F8F8',
          cursor: 'text',
          transition: 'border-color 0.2s ease',
        }}
        className="tag-input-wrapper"
      >
        {tags.map((tag, idx) => (
          <span
            key={`${tag}-${idx}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              backgroundColor: colors.tagBg,
              border: `1px solid ${colors.tagBorder}`,
              borderRadius: '999px',
              padding: '4px 10px 4px 12px',
              fontSize: '13px',
              color: colors.tagText,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              maxWidth: '220px',
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tag}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(idx);
              }}
              aria-label={t('Supprimer')}
              style={{
                background: 'none',
                border: 'none',
                padding: '0 2px',
                cursor: 'pointer',
                color: colors.tagXColor,
                fontSize: '14px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.75,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          className="tag-input-inner"
          type="text"
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value;
            setShowDuplicateWarning(false);
            if (val.endsWith(',')) {
              addTag(val.slice(0, -1));
            } else {
              if (!maxLength) {
                setInputValue(val);
                return;
              }
              const baseLength = tags.join(', ').length;
              const separatorLength = tags.length > 0 ? 2 : 0;
              const allowedInputLength = Math.max(0, maxLength - baseLength - separatorLength);
              setInputValue(val.slice(0, allowedInputLength));
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={tags.length === 0 && inputValue.length === 0 ? placeholderText : ''}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            fontSize: placeholderFontSize,
            color: '#374151',
            minWidth: isEmptyState ? '180px' : '14px',
            width: isEmptyState ? '100%' : `${Math.max(1, inputValue.length + 0.25)}ch`,
            flex: isEmptyState ? '1 1 180px' : '0 1 auto',
            padding: '2px 0',
          }}
        />
        {(tags.length > 0 || inputValue.length > 0) && !isLimitReached && (
          <div
            className="tag-input-persistent-placeholder"
            style={{
              color: '#9CA3AF',
              fontSize: placeholderFontSize,
              marginTop: '2px',
              width: '100%',
              flexBasis: '100%',
              pointerEvents: 'none',
            }}
          >
            {placeholderText}
          </div>
        )}
      </div>

      {isLimitReached && (
        <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px' }}>
          {t('Limite de mots autorisée dépassée')}
        </div>
      )}

      {showDuplicateWarning && !isLimitReached && (
        <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px' }}>
          {t('Ce terme est déjà présent')}
        </div>
      )}

      <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
        {t('Appuyez sur Entrée ou virgule pour ajouter un terme')}
      </div>
    </div>
  );
}

export default TagInput;
